import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Shield, 
  HelpCircle, 
  ChevronDown, 
  ChevronUp, 
  Search, 
  Lock, 
  FileText, 
  Bell, 
  Clock,
  Database,
  Globe,
  ArrowLeft,
  Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const FAQ_CATEGORIES = [
  {
    title: "Getting Started",
    icon: HelpCircle,
    faqs: [
      {
        question: "What is Certificate Transparency (CT)?",
        answer: "Certificate Transparency is an open framework designed to protect against certificate-based threats. It provides an open audit log of all SSL/TLS certificates issued by Certificate Authorities (CAs), making it possible to detect mistakenly or maliciously issued certificates."
      },
      {
        question: "What does this CT Scanner do?",
        answer: "CT Scanner allows you to search Certificate Transparency logs to find all SSL/TLS certificates issued for a domain or organization. You can track which Certificate Authorities have issued certificates, monitor for unauthorized certificates, and analyze your certificate landscape."
      },
      {
        question: "Do I need an account to use the scanner?",
        answer: "No, you can perform scans without an account. However, creating an account allows you to save your scan history, set up alerts, and access your results from any device."
      }
    ]
  },
  {
    title: "Searching & Scanning",
    icon: Search,
    faqs: [
      {
        question: "What's the difference between domain and organization search?",
        answer: "Domain search finds certificates for a specific domain (e.g., 'example.com'). Organization search finds all certificates issued to an organization by name (e.g., 'Google Inc'). Domain search is more precise, while organization search gives a broader view."
      },
      {
        question: "What date ranges can I search?",
        answer: "You can search certificates from the last 7 days, 14 days, 30 days, 3 months, 6 months, 1 year, or select a custom date range using the calendar picker."
      },
      {
        question: "Why do some searches return no results?",
        answer: "This could happen if: (1) no certificates were issued in the selected time period, (2) the domain/organization name doesn't match exactly, or (3) the crt.sh service is temporarily unavailable. Try broadening your date range or checking your search term."
      },
      {
        question: "What do DV, OV, and EV certificates mean?",
        answer: "These are certificate validation levels. DV (Domain Validation) only verifies domain ownership. OV (Organization Validation) also verifies the organization's identity. EV (Extended Validation) has the strictest verification requirements and shows the organization name in browsers."
      }
    ]
  },
  {
    title: "Results & Data",
    icon: Database,
    faqs: [
      {
        question: "What is a CA (Certificate Authority)?",
        answer: "A Certificate Authority is a trusted organization that issues SSL/TLS certificates. Common CAs include Let's Encrypt, DigiCert, Sectigo, and GlobalSign. The CA breakdown shows which authorities have issued certificates for your search."
      },
      {
        question: "What are SANs (Subject Alternative Names)?",
        answer: "SANs are additional domain names included in a single certificate. For example, a certificate for 'example.com' might also include 'www.example.com', 'api.example.com', etc. Wildcard SANs (like '*.example.com') cover all subdomains."
      },
      {
        question: "Can I export my results?",
        answer: "Yes! Click the 'Export CSV' button to download your scan results as a CSV file. This includes the query, certificate counts, CA breakdown, and scan timestamps."
      },
      {
        question: "How do I compare two scans?",
        answer: "Use the Compare feature to track changes over time. Click the 'A' and 'B' buttons next to any two scans to select them, then go to the Compare tab to see which CAs were added, removed, or changed."
      }
    ]
  },
  {
    title: "Alerts",
    icon: Bell,
    faqs: [
      {
        question: "What are certificate alerts?",
        answer: "Alerts notify you when changes are detected in certificates for a domain or organization. You can be alerted when a new Certificate Authority starts issuing certificates, or when the total certificate count changes significantly."
      },
      {
        question: "How do alerts work?",
        answer: "When you create an alert, the system monitors for changes. You can manually check alerts using the 'Check Now' button, and email notifications are sent when alert conditions are met (requires email configuration)."
      },
      {
        question: "Can I get alerts via Slack or Discord?",
        answer: "Webhook integrations for Slack and Discord are on our roadmap. Currently, alerts are sent via email."
      }
    ]
  },
  {
    title: "Account & Security",
    icon: Lock,
    faqs: [
      {
        question: "What are the password requirements?",
        answer: "Passwords must be at least 10 characters long and include: one uppercase letter, one lowercase letter, one number, and one special character. This ensures strong protection for your account."
      },
      {
        question: "How do I reset my password?",
        answer: "Click 'Forgot password?' on the login page, enter your email, and you'll receive a password reset link. The link expires after 1 hour for security."
      },
      {
        question: "Is my data private?",
        answer: "Yes. Your scan history is private to your account when logged in. Scans performed without logging in are not associated with any account. We don't share your data with third parties."
      }
    ]
  },
  {
    title: "Technical & Troubleshooting",
    icon: Globe,
    faqs: [
      {
        question: "Why did my scan fail with a timeout or 503 error?",
        answer: "The CT Scanner uses crt.sh as its data source, which can sometimes be slow or unavailable. The system automatically retries failed requests. You can increase the retry attempts in the settings dropdown."
      },
      {
        question: "What does 'retry attempts' mean?",
        answer: "This setting controls how many times the system will retry if the initial request fails. Higher values (5-10) are more reliable but may take longer. Lower values (1-3) are faster but may fail on busy days."
      },
      {
        question: "Is there a limit on searches?",
        answer: "There's no strict limit, but please be reasonable. Bulk imports are limited to 100 queries per upload, and the system limits each bulk scan to 50 queries. This helps maintain service quality for all users."
      }
    ]
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full py-4 px-2 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="faq-item"
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-2 pb-4 text-muted-foreground text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Scanner
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Help Center</h1>
              <p className="text-muted-foreground">Frequently asked questions about CT Scanner</p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {FAQ_CATEGORIES.map((category) => (
            <Card key={category.title} data-testid="faq-category">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-lg">
                  <category.icon className="w-5 h-5 text-primary" />
                  {category.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {category.faqs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-lg">
              <Mail className="w-5 h-5 text-primary" />
              Still Need Help?
            </CardTitle>
            <CardDescription>
              Can't find what you're looking for? We're here to help.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              If you have questions not covered in our FAQ, please reach out through our contact form. We typically respond within 24-48 hours.
            </p>
            <Link to="/contact">
              <Button className="gap-2" data-testid="contact-button">
                <Mail className="w-4 h-4" />
                Contact Us
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
